'use client';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Fade, IconButton } from '@mui/material';
import type React from 'react';
import { type MouseEvent, type ReactNode, type TouchEvent, useRef, useState } from 'react';

type SlideshowProps = {
  children: ReactNode[];
};

type ArrowContainerProps = {
  children: React.JSX.Element;
};

const DRAG_OFFSET = 250;

const FADE_TIMEOUT = 300;

const SHARED_BUTTON_ICON_STYLES = {
  height: '36px',
  width: '36px',
  backgroundColor: 'common.white',
  borderRadius: '50%',
  color: 'secondary.main',
  position: 'absolute',
  top: '40%',
  zIndex: 10,
  boxShadow: '0 4px 8px 0 rgba(100,102,106,.1),0 1px 4px 0 rgba(100,102,106,.36)',
};

export function Slideshow({ children }: SlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartX = useRef(0);
  const totalSlides = children.length;

  const handlePrev = () => {
    setCurrentIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleNext = () => {
    setCurrentIndex(prevIndex => (prevIndex < totalSlides - 1 ? prevIndex + 1 : prevIndex));
  };

  const isTouchEvent = (evt: MouseEvent | TouchEvent): evt is TouchEvent => {
    return (evt as TouchEvent).touches !== undefined;
  };

  const getEvtClientX = (evt: MouseEvent | TouchEvent) => {
    return isTouchEvent(evt) ? evt.touches[0]?.clientX : evt.clientX;
  };

  const handleDragStart = (evt: MouseEvent | TouchEvent) => {
    dragStartX.current = getEvtClientX(evt);
    setIsDragging(true);
  };

  const handleDragMove = (evt: MouseEvent | TouchEvent) => {
    if (!isDragging) return;

    const currentX = getEvtClientX(evt);
    const offset = currentX - dragStartX.current;
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    if (!isDragging || dragStartX.current === 0) return;

    setIsDragging(false);

    if (dragOffset > DRAG_OFFSET) {
      handlePrev();
    } else {
      handleNext();
    }

    setDragOffset(0);
    dragStartX.current = 0;
  };

  const ArrowContainer = ({ children }: ArrowContainerProps) => (
    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{children}</Box>
  );

  return (
    <Box>
      <Box sx={{ position: 'relative' }}>
        <ArrowContainer>
          <Fade in={currentIndex > 0} timeout={FADE_TIMEOUT}>
            <IconButton
              aria-label="Previous slide"
              onClick={handlePrev}
              disableRipple
              sx={{
                ...SHARED_BUTTON_ICON_STYLES,
                borderTopRightRadius: '50%',
                borderBottomRightRadius: '50%',
                left: { xs: '-0.5rem', md: '-1.5rem' },
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>
          </Fade>
        </ArrowContainer>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            userSelect: 'none',
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onMouseMove={handleDragMove}
          onTouchMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchEnd={handleDragEnd}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
              transform: `translateX(calc(-${(currentIndex * 100) / totalSlides}% + ${dragOffset}px))`,
              width: `${totalSlides * 100}%`,
            }}
          >
            {children.map((child, index) => (
              <Box key={index} sx={{ paddingRight: 3, width: '100%' }}>
                {child}
              </Box>
            ))}
          </Box>
        </Box>
        <ArrowContainer>
          <Fade in={currentIndex < totalSlides - 1} timeout={FADE_TIMEOUT}>
            <IconButton
              aria-label="Next slide"
              onClick={handleNext}
              disableRipple
              sx={{
                ...SHARED_BUTTON_ICON_STYLES,
                borderTopLeftRadius: '50%',
                borderBottomLeftRadius: '50%',
                right: 0,
              }}
            >
              <KeyboardArrowRight />
            </IconButton>
          </Fade>
        </ArrowContainer>
      </Box>
      {totalSlides > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 4,
          }}
        >
          {Array.from({ length: totalSlides }, (_, index) => (
            <Box
              onClick={() => setCurrentIndex(index)}
              sx={{ width: '48px', height: '48px', cursor: 'pointer' }}
              key={`slider-${index}`}
            >
              <Box
                sx={{
                  flexGrow: 0,
                  bgcolor: index === currentIndex ? 'primary.main' : 'transparent',
                  borderColor: 'neutral.main',
                  borderWidth: `${index === currentIndex ? 0 : '1px'}`,
                  transition: 'background-color 0.2s ease-in, background-color 0.5s ease-out',
                  borderStyle: 'solid',
                  height: '10px',
                  width: '10px',
                  borderRadius: '50%',
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
